import {type KeyboardEvent, useState} from 'react';

import {useTranslation} from 'react-i18next';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Box, Collapse, Typography} from '@mui/material';

import {
  culturalFitExpandIconSx,
  culturalFitPaperSx,
  getCulturalFitHeaderSx,
} from './workforceProfileCulturalFitAnalysisSectionSx';
import {WorkforceProfileInferredCulturalAnswerCards} from './WorkforceProfileInferredCulturalAnswerCards';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {InfoBanner} from '../../../../../components/InfoBanner/InfoBanner';
import {semantic} from '../../../../../layouts/theme/designSystem';
import {WorkforceProfileInferredCulturalAnswer} from '../../types/WorkforceProfileTypes';

export interface Props {
  answers: WorkforceProfileInferredCulturalAnswer[];
  alwaysExpanded?: boolean;
}

export const WorkforceProfileInferredCulturalSection = ({answers, alwaysExpanded}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail.inferredCultural'});
  const [expanded, setExpanded] = useState(!!alwaysExpanded);

  if (answers.length === 0) {
    return null;
  }

  const isOpen = alwaysExpanded || expanded;

  const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (alwaysExpanded) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setExpanded((previous) => !previous);
    }
  };

  const innerContent = (
    <Box>
      <InfoBanner text={t('description')} sx={{mb: 2.5}} />
      <WorkforceProfileInferredCulturalAnswerCards answers={answers} />
    </Box>
  );

  return (
    <EmpionPaper sx={culturalFitPaperSx}>
      <Box
        onClick={alwaysExpanded ? undefined : () => setExpanded((previous) => !previous)}
        onKeyDown={handleHeaderKeyDown}
        role={alwaysExpanded ? undefined : 'button'}
        tabIndex={alwaysExpanded ? undefined : 0}
        aria-expanded={alwaysExpanded ? undefined : expanded}
        sx={getCulturalFitHeaderSx(isOpen, !!alwaysExpanded)}
      >
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography variant='subtitle2' sx={{fontWeight: 600}}>
            {t('title')}
          </Typography>
          {!isOpen && (
            <Typography variant='caption' color='text.secondary' sx={{display: 'block', mt: 0.5, lineHeight: 1.45}}>
              {t('collapsedHint')}
            </Typography>
          )}
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
          {!isOpen && (
            <Typography variant='caption' sx={{fontWeight: 600, color: semantic.info.fg}}>
              {t('dimensionCount', {count: answers.length})}
            </Typography>
          )}
          {!alwaysExpanded && <ExpandMoreIcon sx={culturalFitExpandIconSx(expanded)} />}
        </Box>
      </Box>

      {alwaysExpanded ? innerContent : (
        <Collapse in={expanded} timeout='auto' unmountOnExit>
          {innerContent}
        </Collapse>
      )}
    </EmpionPaper>
  );
};
